import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Snackbar,
  Alert,
  Pagination,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import {Plus, ArrowUp, ArrowDown} from 'lucide-react';
import SearchGreyIcon from '../../assets/search-grey.svg';
import {SettingsCard} from '../../components/SettingsCard';
import {useSelector} from 'react-redux';
import {useTheme} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';
import AddCompanyModal from './AddCompanyModal';
import {
  useGetChildOrgsQuery,
  useJoinOrganizationMutation,
  useLeaveOrganizationMutation,
} from '../../data/api';
import Loading from '../../components/Loading';
import PropTypes from 'prop-types';
import {withRequiredAuthInfo} from '@propelauth/react';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    minHeight: '48px',
  },
  title: {
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '130%',
    letterSpacing: '-0.24px',
    color: '#162523',
  },
  searchContainer: {
    marginBottom: '24px',
  },
  searchField: {
    display: 'flex',
    width: '250px',
    padding: '8px 12px',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    border: '1px solid #E1E5E4',
    backgroundColor: '#FFF',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    width: '100%',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.14px',
    '&::placeholder': {
      color: '#9AA7A6',
    },
  },
  tableContainer: {
    boxShadow: 'none',
    '& .MuiTable-root': {
      borderCollapse: 'separate',
    },
  },
  tableHeader: {
    backgroundColor: 'transparent',
    '& .MuiTableCell-head': {
      color: '#819190',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '150%',
      letterSpacing: '-0.14px',
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent',
    },
  },
  tableRow: {
    backgroundColor: 'transparent !important',
    '&:nth-of-type(odd)': {
      backgroundColor: 'transparent !important',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent !important',
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  emptyStateText: {
    color: '#819190',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '150%',
    letterSpacing: '-0.14px',
    textAlign: 'center',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
    '& .MuiPagination-ul': {
      '& .MuiPaginationItem-root': {
        color: '#819190',
        '&.Mui-selected': {
          backgroundColor: '#26A993',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#26A993',
          },
        },
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      },
    },
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: '#162523',
    },
  },
  sortIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '4px',
  },
};

const OrganizationClientsPage = ({accessHelper}) => {
  const {parentOrg, clientOrgs} = useSelector((state) => state.session);
  const theme = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [processingOrgId, setProcessingOrgId] = useState(null);
  const pageSize = 25;

  const [joinOrganization] = useJoinOrganizationMutation();
  const [leaveOrganization] = useLeaveOrganizationMutation();

  const hasOwnerPermissions =
    parentOrg &&
    (parentOrg.userAssignedRole === 'InScope Admin' ||
      parentOrg.userAssignedRole === 'Owner');

  const hasViewPsoOrgsPermission =
    parentOrg &&
    accessHelper.hasPermission(parentOrg.orgId, 'can_view_pso_orgs');

  // User can access if they have owner permissions OR view PSO orgs permission
  const hasAccess = hasOwnerPermissions || hasViewPsoOrgsPermission;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: childOrgsData,
    isLoading,
    error,
  } = useGetChildOrgsQuery(
    {
      parentOrganizationId: parentOrg?.orgId,
      page,
      pageSize,
      search: debouncedSearch,
      sortBy,
      sortOrder,
    },
    {
      skip: !parentOrg?.orgId,
    }
  );

  const pageTitle = parentOrg
    ? `${parentOrg.orgName} Clients`
    : 'Organization Clients';

  const handleShowSnackbar = (snackbarData) => {
    setSnackbar({
      open: true,
      message: snackbarData.message,
      severity: snackbarData.severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({...prev, open: false}));
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setAddClientModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddClientModalOpen(false);
    setEditingClient(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleJoinOrganization = async (orgId) => {
    setProcessingOrgId(orgId);
    try {
      await joinOrganization({orgId}).unwrap();
      handleShowSnackbar({
        message: 'Successfully joined organization',
        severity: 'success',
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      setProcessingOrgId(null);
      handleShowSnackbar({
        message: error?.data?.error || 'Failed to join organization',
        severity: 'error',
      });
    }
  };

  const handleLeaveOrganization = async (orgId) => {
    setProcessingOrgId(orgId);
    try {
      await leaveOrganization({orgId}).unwrap();
      handleShowSnackbar({
        message: 'Successfully left organization',
        severity: 'success',
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      setProcessingOrgId(null);
      handleShowSnackbar({
        message: error?.data?.error || 'Failed to leave organization',
        severity: 'error',
      });
    }
  };

  const isUserMemberOfOrg = (orgId) => {
    return clientOrgs.some((org) => org.orgId === orgId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const SortableHeader = ({field, label, currentSortBy, currentSortOrder}) => {
    const isActive = currentSortBy === field;
    const isAsc = isActive && currentSortOrder === 'asc';

    return (
      <TableCell onClick={() => handleSort(field)} sx={styles.sortableHeader}>
        {label}
        <Box component="span" sx={styles.sortIcon}>
          {isActive && isAsc ? (
            <ArrowUp size={14} style={{color: '#162523'}} />
          ) : isActive && !isAsc ? (
            <ArrowDown size={14} style={{color: '#162523'}} />
          ) : (
            <ArrowUp size={14} style={{color: '#819190', opacity: 0.5}} />
          )}
        </Box>
      </TableCell>
    );
  };

  SortableHeader.propTypes = {
    field: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    currentSortBy: PropTypes.string.isRequired,
    currentSortOrder: PropTypes.oneOf(['asc', 'desc']).isRequired,
  };

  const totalPages = childOrgsData?.pagination?.totalPages || 0;
  const totalItems = childOrgsData?.pagination?.totalItems || 0;

  if (!parentOrg) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4">Organization not found</Typography>
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4" sx={{color: '#D73E3E', marginBottom: '16px'}}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{color: '#666', marginBottom: '24px'}}>
          You do not have permission to access settings for this organization.
        </Typography>
        <Button
          onClick={() => navigate('/settings')}
          sx={theme.button.primary.medium}
        >
          Back to Settings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Page Header */}
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{pageTitle}</Typography>
        {hasOwnerPermissions && (
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => setAddClientModalOpen(true)}
            sx={{
              ...theme.button.primary.medium,
              gap: '3px',
              alignSelf: 'flex-start',
              marginTop: '2px', // Fine-tune alignment with title baseline
            }}
          >
            Add new client
          </Button>
        )}
      </Box>

      <Box sx={{paddingBottom: '80px'}}>
        {/* Search and Client Count */}
        <Box
          sx={{
            ...styles.searchContainer,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={styles.searchField}>
            <img src={SearchGreyIcon} height={20} width={20} alt="search" />
            <Box
              component="input"
              type="text"
              placeholder="Search clients"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={styles.searchInput}
            />
            {search && (
              <CloseIcon
                sx={{
                  fontSize: 16,
                  color: '#9AA7A6',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#162523',
                  },
                }}
                onClick={() => setSearch('')}
              />
            )}
          </Box>

          {/* Client Count */}
          {!isLoading && !error && totalItems !== undefined && (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#819190',
                fontWeight: 500,
                transform: 'translate(-6px, 6px)',
              }}
            >
              {totalItems} {totalItems === 1 ? 'client' : 'clients'}
            </Typography>
          )}
        </Box>

        {/* Clients Table */}
        <SettingsCard>
          {error && (
            <Alert severity="error" sx={{marginBottom: '16px'}}>
              Failed to load clients. Please try again.
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box
              sx={{width: '100%', display: 'flex', justifyContent: 'center'}}
            >
              <Loading
                loading={true}
                message="Loading clients..."
                containerProps={{
                  height: '300px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 'none',
                }}
              />
            </Box>
          )}

          {/* Clients Table */}
          {!isLoading && !error && (
            <>
              <TableContainer sx={styles.tableContainer}>
                <Table>
                  <TableHead sx={styles.tableHeader}>
                    <TableRow>
                      <SortableHeader
                        field="name"
                        label="Name"
                        currentSortBy={sortBy}
                        currentSortOrder={sortOrder}
                      />
                      <TableCell>Admin(s)</TableCell>
                      <SortableHeader
                        field="createdAt"
                        label="Created date"
                        currentSortBy={sortBy}
                        currentSortOrder={sortOrder}
                      />
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!childOrgsData?.orgs || childOrgsData.orgs.length === 0 ? (
                      <TableRow sx={styles.tableRow}>
                        <TableCell colSpan={4} align="center">
                          <Typography sx={styles.emptyStateText}>
                            {debouncedSearch
                              ? 'No clients found matching your search'
                              : 'No clients found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      childOrgsData.orgs.map((client) => (
                        <TableRow
                          key={client.orgId || client.org}
                          sx={styles.tableRow}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                color: '#162523',
                                fontSize: '16px',
                                fontWeight: 500,
                                lineHeight: '130%',
                                letterSpacing: '-0.14px',
                              }}
                            >
                              {client.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                color:
                                  client.admins && client.admins.length > 0
                                    ? '#162523'
                                    : '#819190',
                                fontSize:
                                  client.admins && client.admins.length > 0
                                    ? '16px'
                                    : '14px',
                                fontWeight: 500,
                                lineHeight:
                                  client.admins && client.admins.length > 0
                                    ? '130%'
                                    : '140%',
                                letterSpacing:
                                  client.admins && client.admins.length > 0
                                    ? '-0.14px'
                                    : '-0.12px',
                              }}
                            >
                              {client.admins && client.admins.length > 0
                                ? client.admins
                                    .map(
                                      (admin) =>
                                        `${admin.firstName} ${admin.lastName}`
                                    )
                                    .join(', ')
                                : 'None'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                color: '#819190',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '140%',
                                letterSpacing: '-0.12px',
                              }}
                            >
                              {formatDate(client.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {hasOwnerPermissions ? (
                              <Button
                                variant="outlined"
                                onClick={() => handleEditClient(client)}
                                sx={theme.button.outline.large}
                              >
                                Update
                              </Button>
                            ) : hasViewPsoOrgsPermission ? (
                              isUserMemberOfOrg(client.orgId || client.org) ? (
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    handleLeaveOrganization(
                                      client.orgId || client.org
                                    )
                                  }
                                  disabled={
                                    processingOrgId ===
                                    (client.orgId || client.org)
                                  }
                                  sx={theme.button.outline.large}
                                >
                                  {processingOrgId ===
                                  (client.orgId || client.org)
                                    ? 'Leaving...'
                                    : 'Leave'}
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  onClick={() =>
                                    handleJoinOrganization(
                                      client.orgId || client.org
                                    )
                                  }
                                  disabled={
                                    processingOrgId ===
                                    (client.orgId || client.org)
                                  }
                                  sx={theme.button.primary.medium}
                                >
                                  {processingOrgId ===
                                  (client.orgId || client.org)
                                    ? 'Joining...'
                                    : 'Join'}
                                </Button>
                              )
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={styles.paginationContainer}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </SettingsCard>

        {/* Add/Edit Client Modal - Only for owners */}
        {hasOwnerPermissions && (
          <AddCompanyModal
            open={addClientModalOpen}
            onClose={handleCloseModal}
            onShowSnackbar={handleShowSnackbar}
            editingClient={editingClient}
          />
        )}

        {/* Snackbar for success and error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}
          style={{marginTop: '35px'}}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            icon={
              snackbar.severity === 'success' ? (
                <Box
                  sx={{
                    backgroundColor: '#26A993',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CheckIcon sx={{color: 'white', fontSize: 16}} />
                </Box>
              ) : (
                <ErrorOutlineIcon sx={{color: '#D73E3E', fontSize: 22}} />
              )
            }
            sx={{
              width: '100%',
              backgroundColor: '#FFFFFF',
              color: '#000000',
              border: '1px solid #E1E5E4',
              borderRadius: '8px',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

OrganizationClientsPage.propTypes = {
  accessHelper: PropTypes.shape({
    isRole: PropTypes.func.isRequired,
    isAtLeastRole: PropTypes.func.isRequired,
    hasPermission: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRequiredAuthInfo(OrganizationClientsPage);
